// Main entry point for player designation system
// Provides clean, sport-agnostic API for generating player designations

import type { PlayerAttribute } from '../../types/playerCard';
import type { DesignationResult } from './types';
import { getPositionRecommendations } from '../../data/positionRecommendations';
import { getPlayerDesignations } from '../../data/playerDesignations';
import type { Sport } from '../../types';

/**
 * Generate separate offense and defense designations for a player
 * Returns an object with offense_designation and defense_designation
 * Each side is calculated independently based on relevant positions
 * 
 * Uses the configuration-based system from playerDesignations.ts
 * All sport-specific rules are defined in configuration files, not hardcoded
 */
export function generateOffenseDefenseDesignations(
  attributes: PlayerAttribute[],
  positions: string[],
  roleTypes: string[],
  sport: string = 'Football'
): DesignationResult {
  if (attributes.length === 0) {
    return { offense_designation: null, defense_designation: null };
  }

  // Convert to format expected by getPlayerDesignations
  const attributeArray = attributes.map(attr => ({
    attribute_key: attr.attribute_key,
    value: attr.value,
  }));

  return getPlayerDesignations(attributeArray, positions, roleTypes, sport as Sport);
}

/**
 * Generate a single designation for a player (legacy support)
 * Returns the designation with the most matching attributes (requires at least 3 matches)
 * If multiple have the same match count, uses priority as tiebreaker (deterministic)
 * 
 * @deprecated Use generateOffenseDefenseDesignations instead for separate offense/defense designations
 */
export function generateDesignation(
  attributes: PlayerAttribute[],
  positions: string[],
  roleTypes: string[],
  sport: string = 'Football'
): string | null {
  if (attributes.length === 0) {
    return null;
  }

  // Use the new system and return offense designation (or defense if offense is null)
  const result = generateOffenseDefenseDesignations(attributes, positions, roleTypes, sport);
  return result.offense_designation || result.defense_designation;
}

/**
 * Get suggested position groups based on attributes
 * Uses the position recommendation system from positionRecommendations.ts
 * All sport-specific rules are defined in configuration files, not hardcoded
 */
export function getSuggestedPositions(
  attributes: PlayerAttribute[],
  sport: string = 'Football'
): string[] {
  return getPositionRecommendations(attributes, sport as Sport);
}

// Export types for use in other modules
export type { DesignationResult };
export type { DesignationRule, PositionCategories } from './types';

